import { useState, useEffect, useMemo, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import JornadaTrabalhadaService from "../../services/resource/jornadaTrabalhadaService";
import FolhaPagamentoService from "../../services/resource/FolhaPagamentoService";
import { useNavigate } from "react-router-dom";

// ─── Utilitários ──────────────────────────────────────────────────────────────

const MESES_LABEL = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const fmtMoeda = (v = 0) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtHoras = (v = 0) => {
    const h = Math.floor(v);
    const m = Math.round((v - h) * 60);
    return `${h}h ${m > 0 ? m + "min" : ""}`.trim();
};

const mesLabel = (ano, mes) => `${MESES_LABEL[(mes ?? 1) - 1]}/${String(ano).slice(2)}`;

const CHART_COLORS = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#0EA5E9", "#8B5CF6", "#EA580C"];

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function SectionDivider({ label }) {
    return (
        <div className="flex items-center gap-3 my-1 mt-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
            <div className="flex-1 h-px bg-border" />
        </div>
    );
}

function KPICard({ label, value, sub, icon, color, loading }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-4 shadow-card relative overflow-hidden flex flex-col gap-1">
            <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl" style={{ background: color }} />
            <span className="text-xl">{icon}</span>
            {loading
                ? <div className="h-8 w-3/4 bg-muted animate-pulse rounded-lg mt-1" />
                : <span className="text-2xl font-bold tracking-tight text-foreground mt-1 leading-none">{value}</span>
            }
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
            {sub && <span className="text-xs text-muted-foreground mt-0.5">{sub}</span>}
        </div>
    );
}

function ChartCard({ title, accent, loading, children }) {
    return (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-card">
            <div className="flex items-center gap-2 px-3 py-3.5 border-b border-border bg-muted">
                {accent && <div className="w-0.5 h-4 rounded-full shrink-0" style={{ background: accent }} />}
                <h3 className="text-sm font-bold text-foreground">{title}</h3>
            </div>
            <div className="p-3">
                {loading
                    ? <div className="flex items-center justify-center h-52">
                        <div className="space-y-3 w-full">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="h-8 bg-muted animate-pulse rounded-lg" style={{ width: `${80 - i * 15}%` }} />
                            ))}
                        </div>
                    </div>
                    : children
                }
            </div>
        </div>
    );
}

// ─── Gráficos ─────────────────────────────────────────────────────────────────

// Evolução mensal — dual axis: valor (R$) + horas
function ChartEvolucaoMensal({ dados = [], colorValor, colorHoras, loading, title, accent }) {
    const cats = dados.map(d => mesLabel(d.ano, d.mes));
    const serieValor = dados.map(d => +(d.valor ?? 0).toFixed(2));
    const serieHoras = dados.map(d => +(d.horas ?? 0).toFixed(2));

    const options = {
        chart: { type: "line", toolbar: { show: false }, animations: { enabled: true, speed: 700 } },
        stroke: { curve: "smooth", width: [3, 2], dashArray: [0, 5] },
        markers: { size: 4, hover: { size: 6 } },
        colors: [colorValor, colorHoras],
        xaxis: {
            categories: cats,
            labels: { style: { colors: "#747577", fontSize: "11px" } },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: [
            {
                seriesName: "Valor (R$)",
                labels: {
                    style: { colors: colorValor, fontSize: "11px" },
                    formatter: v => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }),
                },
            },
            {
                seriesName: "Horas",
                opposite: true,
                labels: {
                    style: { colors: colorHoras, fontSize: "11px" },
                    formatter: v => `${v.toFixed(0)}h`,
                },
            },
        ],
        grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
        tooltip: {
            theme: "light",
            y: [
                { formatter: v => fmtMoeda(v) },
                { formatter: v => fmtHoras(v) },
            ],
        },
        legend: { labels: { colors: "#64748b" }, position: "top" },
        dataLabels: { enabled: false },
    };

    return (
        <ChartCard title={title} accent={accent} loading={loading}>
            <ReactApexChart
                type="line"
                height={260}
                series={[
                    { name: "Valor (R$)", data: serieValor },
                    { name: "Horas", data: serieHoras },
                ]}
                options={options}
            />
        </ChartCard>
    );
}

// Ranking filiais — bar horizontal
function ChartRankingFiliais({ dados = [], loading }) {
    const sorted = [...dados].sort((a, b) => b.valor - a.valor);
    const labels = sorted.map(d => d.filial);
    const valores = sorted.map(d => +(d.valor ?? 0).toFixed(2));
    const horas = sorted.map(d => +(d.horas ?? 0).toFixed(2));

    const options = {
        chart: { type: "bar", toolbar: { show: false }, animations: { speed: 700 } },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%", distributed: false } },
        colors: ["#6366F1", "#10B981"],
        xaxis: {
            categories: labels,
            labels: {
                style: { colors: "#747577", fontSize: "10px" },
                formatter: v => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }),
            },
            axisBorder: { show: false },
        },
        yaxis: { labels: { style: { colors: "#64748b", fontSize: "11px" } } },
        grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: {
            theme: "light",
            y: [
                { formatter: v => fmtMoeda(v) },
                { formatter: v => fmtHoras(v) },
            ],
        },
        legend: { labels: { colors: "#64748b" }, position: "top" },
    };

    return (
        <ChartCard title="Ranking por Filial" accent="#6366F1" loading={loading}>
            <ReactApexChart
                type="bar"
                height={Math.max(220, dados.length * 65)}
                series={[
                    { name: "Valor (R$)", data: valores },
                    { name: "Horas", data: horas },
                ]}
                options={options}
            />
        </ChartCard>
    );
}

// Participação filiais — donut
function ChartParticipacaoFiliais({ dados = [], loading }) {
    const labels = dados.map(d => d.filial);
    const valores = dados.map(d => +(d.valor ?? 0).toFixed(2));
    const total = valores.reduce((a, b) => a + b, 0);

    const options = {
        chart: { type: "donut", toolbar: { show: false } },
        labels,
        colors: CHART_COLORS,
        stroke: { width: 0 },
        plotOptions: {
            pie: {
                donut: {
                    size: "52%",
                    labels: {
                        show: true,
                        total: {
                            show: true, label: "Total", color: "#747577", fontSize: "12px",
                            formatter: () => fmtMoeda(total),
                        },
                    },
                },
            },
        },
        legend: { position: "right", labels: { colors: "#64748b" }, fontSize: "11px" },
        dataLabels: { enabled: true, style: { fontSize: "10px" }, formatter: (v) => `${v.toFixed(1)}%` },
        tooltip: { theme: "light", y: { formatter: v => fmtMoeda(v) } },
    };

    return (
        <ChartCard title="Participação por Filial" accent="#F59E0B" loading={loading}>
            <ReactApexChart type="donut" height={260} series={valores} options={options} />
        </ChartCard>
    );
}

// Comparativo Ano Anterior — grouped bar
function ChartComparativoAno({ dados = [], loading }) {
    const cats = dados.map(d => mesLabel(d.ano, d.mes));
    const atual = dados.map(d => +(d.valorAtual ?? 0).toFixed(2));
    const ant = dados.map(d => +(d.valorAnterior ?? 0).toFixed(2));
    const pct = dados.map(d => +(d.percentual ?? 0).toFixed(2));

    const options = {
        chart: { type: "bar", toolbar: { show: false }, animations: { speed: 700 } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%", grouped: true } },
        colors: ["#6366F1", "#747577"],
        xaxis: {
            categories: cats,
            labels: { style: { colors: "#747577", fontSize: "11px" } },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: { colors: "#747577", fontSize: "11px" },
                formatter: v => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }),
            },
        },
        grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: {
            theme: "light",
            y: { formatter: v => fmtMoeda(v) },
            custom: ({ series, seriesIndex, dataPointIndex }) => {
                const p = pct[dataPointIndex];
                const color = p > 0 ? "#ef4444" : p < 0 ? "#10b981" : "#747577";
                const sinal = p > 0 ? "▲" : p < 0 ? "▼" : "=";
                return `<div class="p-2 text-xs">
                    <div><b>${cats[dataPointIndex]}</b></div>
                    <div>Atual: ${fmtMoeda(series[0][dataPointIndex])}</div>
                    <div>Anterior: ${fmtMoeda(series[1][dataPointIndex])}</div>
                    <div style="color:${color};font-weight:700">${sinal} ${Math.abs(p).toFixed(2)}%</div>
                </div>`;
            },
        },
        legend: { labels: { colors: "#64748b" }, position: "top" },
    };

    return (
        <ChartCard title="Comparativo com Ano Anterior" accent="#EF4444" loading={loading}>
            <ReactApexChart
                type="bar"
                height={260}
                series={[
                    { name: "Ano Atual", data: atual },
                    { name: "Ano Anterior", data: ant },
                ]}
                options={options}
            />

            {/* Mini-tabela de variação */}
            {dados.length > 0 && (
                <div className="mt-4 border-t border-border pt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {dados.map((d) => {
                        const up = d.percentual > 0;
                        const neutro = d.percentual === 0;
                        return (
                            <div key={`${d.ano}-${d.mes}`} className="text-center">
                                <p className="text-[10px] text-muted-foreground font-semibold">{mesLabel(d.ano, d.mes)}</p>
                                <p className={`text-sm font-bold ${neutro ? "text-muted-foreground" : up ? "text-red-500" : "text-emerald-600"}`}>
                                    {neutro ? "=" : up ? "▲" : "▼"} {Math.abs(d.percentual).toFixed(1)}%
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}
        </ChartCard>
    );
}

// ─── Dashboard Principal ──────────────────────────────────────────────────────

const ANOS = [2025, 2026];
const MESES_OPC = [
    { label: "Todos os meses", value: "" },
    ...MESES_LABEL.map((l, i) => ({ label: l, value: i + 1 })),
];

export default function DashboardHorasExtras() {
    const service = new FolhaPagamentoService();
    const navigate = useNavigate();

    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filtros, setFiltros] = useState({
        ano: new Date().getFullYear(),
        mes: "",
        filialId: "",
    });
    // lista de filiais para o select — preenchida na primeira carga
    const [filiais, setFiliais] = useState([]);

    const alterarFiltro = (campo, valor) =>
        setFiltros(prev => ({ ...prev, [campo]: valor }));

    const fetchDados = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                ano: filtros.ano || undefined,
                mes: filtros.mes || undefined,
                filialId: filtros.filialId || undefined,
            };
            const res = await service.dashboardHorasExtras(params);
            setDados(res.data);

            // popula lista de filiais a partir do ranking (só na primeira vez)
            if (!filiais.length && res.data?.rankingFiliais?.length) {
                setFiliais(res.data.rankingFiliais.map(f => ({ label: f.filial, value: f.filialId })));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [filtros]);

    useEffect(() => { fetchDados(); }, []);

    const resumo = dados?.resumo ?? {};

    return (
        <div className="min-h-screen bg-background font-sans text-foreground">

            {/* ── Topbar ─────────────────────────────────────────────────── */}
            <header className="sticky top-0 z-50 bg-card border-b border-border shadow-xs">
                <div className="flex items-center justify-between px-6 h-14">
                    <div className="flex items-center gap-3.5">
                        <div className="font-display w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-primary-foreground text-xs font-extrabold tracking-tight shrink-0">
                            HE
                        </div>
                        <div>
                            <p className="text-sm font-bold text-foreground leading-tight">Horas Extras & Banco de Horas</p>
                            <p className="text-[11px] text-muted-foreground">Análise de custos e evolução</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">

                        <button
                            onClick={() => navigate("/jornada-trabalhada")}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/70 disabled:opacity-50 text-foreground text-xs font-semibold rounded-xl shadow-xs transition-colors duration-150"
                        >
                            <i className="fa fa-external-link-square" />
                            Abrir Acompanhamento de Jornada
                        </button>


                    <button
                        onClick={fetchDados}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-primary hover:brightness-95 disabled:opacity-50 text-primary-foreground text-xs font-semibold rounded-xl shadow-xs transition-all duration-150"
                    >
                        <i className={loading ? "fa fa-spinner fa-spin" : "fa fa-search"} />
                        {loading ? "Carregando…" : "Pesquisar"}
                    </button>
                    </div>

                </div>
            </header>

            <main className="px-5 py-5 space-y-4 max-w-screen-2xl mx-auto">

                {/* ── Filtros ────────────────────────────────────────────── */}
                <div className="bg-card border border-border rounded-2xl px-5 py-4 shadow-card">
                    <div className="flex flex-wrap items-end gap-3">

                        {/* Ano */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Ano</label>
                            <select
                                value={filtros.ano}
                                onChange={e => alterarFiltro("ano", Number(e.target.value))}
                                className="h-9 px-3 text-sm border border-input rounded-xl bg-muted text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-150 cursor-pointer"
                            >
                                {ANOS.map(a => <option key={a} value={a}>{a}</option>)}
                            </select>
                        </div>

                        {/* Mês */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Mês</label>
                            <select
                                value={filtros.mes}
                                onChange={e => alterarFiltro("mes", e.target.value)}
                                className="h-9 px-3 text-sm border border-input rounded-xl bg-muted text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-150 cursor-pointer"
                            >
                                {MESES_OPC.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>

                        {/* Filial */}
                        <div className="flex flex-col gap-1 min-w-[200px]">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Filial</label>
                            <select
                                value={filtros.filialId}
                                onChange={e => alterarFiltro("filialId", e.target.value)}
                                className="h-9 px-3 text-sm border border-input rounded-xl bg-muted text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-150 cursor-pointer"
                            >
                                <option value="">Todas as filiais</option>
                                {filiais.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                        </div>

                        <button
                            onClick={fetchDados}
                            disabled={loading}
                            className="self-end flex items-center gap-2 h-9 px-4 bg-primary hover:brightness-95 disabled:opacity-50 text-primary-foreground text-xs font-semibold rounded-xl shadow-xs transition-all duration-150"
                        >
                            <i className={loading ? "fa fa-spinner fa-spin" : "fa fa-search"} />
                            Pesquisar
                        </button>
                    </div>
                </div>

                {/* ── KPIs ───────────────────────────────────────────────── */}
                <SectionDivider label="Resumo do período" />

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KPICard
                        label="Custo Horas Extras"
                        value={fmtMoeda(resumo.valorHoraExtra ?? 0)}
                        sub={`${fmtHoras(resumo.horasExtras ?? 0)} registradas`}
                        icon="⚡"
                        color="#6366F1"
                        loading={loading}
                    />
                    <KPICard
                        label="Horas Extras"
                        value={fmtHoras(resumo.horasExtras ?? 0)}
                        sub={`Custo médio ${fmtMoeda((resumo.valorHoraExtra ?? 0) / Math.max(resumo.horasExtras ?? 1, 1))}/h`}
                        icon="⏱️"
                        color="#F59E0B"
                        loading={loading}
                    />
                    <KPICard
                        label="Custo Banco de Horas"
                        value={fmtMoeda(resumo.valorBancoHoras ?? 0)}
                        sub={`${fmtHoras(resumo.horasBancoHoras ?? 0)} acumuladas`}
                        icon="🏦"
                        color="#10B981"
                        loading={loading}
                    />
                    <KPICard
                        label="Banco de Horas"
                        value={fmtHoras(resumo.horasBancoHoras ?? 0)}
                        sub={`Custo médio ${fmtMoeda((resumo.valorBancoHoras ?? 0) / Math.max(resumo.horasBancoHoras ?? 1, 1))}/h`}
                        icon="📦"
                        color="#0EA5E9"
                        loading={loading}
                    />
                </div>

                {/* ── Evolução mensal ─────────────────────────────────────── */}
                <SectionDivider label="Evolução mensal" />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <ChartEvolucaoMensal
                        title="Evolução — Horas Extras"
                        dados={dados?.evolucaoHoraExtra ?? []}
                        colorValor="#6366F1"
                        colorHoras="#F59E0B"
                        accent="#6366F1"
                        loading={loading}
                    />
                    <ChartEvolucaoMensal
                        title="Evolução — Banco de Horas"
                        dados={dados?.evolucaoBancoHoras ?? []}
                        colorValor="#10B981"
                        colorHoras="#0EA5E9"
                        accent="#10B981"
                        loading={loading}
                    />
                </div>

                {/* ── Filiais ─────────────────────────────────────────────── */}
                <SectionDivider label="Análise por filial" />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <ChartRankingFiliais
                        dados={dados?.rankingFiliais ?? []}
                        loading={loading}
                    />
                    <ChartParticipacaoFiliais
                        dados={dados?.participacaoFiliais ?? []}
                        loading={loading}
                    />
                </div>

                {/* ── Comparativo ano anterior ─────────────────────────────── */}
                <SectionDivider label="Comparativo ano anterior" />

                <ChartComparativoAno
                    dados={dados?.comparativoAnoAnterior ?? []}
                    loading={loading}
                />

            </main>
        </div>
    );
}